import { useQuery, MutationTuple, ApolloQueryResult } from "@apollo/client";
import { FormikHelpers, Formik, Form } from "formik";
import _ from "lodash";
import { useConfirm } from "material-ui-confirm";
import { useSnackbar } from "notistack";
import { useState } from "react";
import * as yup from "yup";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  makeStyles,
  Tooltip,
  useTheme
} from "@material-ui/core";
import PauseIcon from "@material-ui/icons/PauseCircleFilled";
import UnpauseIcon from "@material-ui/icons/PlayCircleFilled";
import { Alert } from "@material-ui/lab";

import ClickToCopy from "src/components/ClickToCopy";
import DisabledButtonWithTooltip from "src/components/DisabledButtonWithTooltip";
import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import { useAdmin } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import SubmitButton from "src/components/form/SubmitButton";
import { ROTATE_CAMPAIGN_JOIN_TOKEN } from "src/graphql/campaignJoinToken";
import {
  FORCE_ROTATE_CAMPAIGN_PHONE_NUMBER,
  EDIT_ROTATABLE_CAMPAIGN_PHONE_NUMBER,
  GET_CAMPAIGN_PHONE_NUMBER_DETAILS,
  EDIT_OUTBOUND_POOL_SIZE
} from "src/graphql/campaignPhoneNumberManagement";
import {
  campaignEditAutoRotatablePhoneNumber,
  campaignEditAutoRotatablePhoneNumberVariables
} from "src/graphql/generated/campaignEditAutoRotatablePhoneNumber";
import {
  campaignEditOutboundPoolSize,
  campaignEditOutboundPoolSizeVariables
} from "src/graphql/generated/campaignEditOutboundPoolSize";
import {
  campaignPhoneNumberDetails,
  campaignPhoneNumberDetailsVariables,
  campaignPhoneNumberDetails_campaign_outboundPhoneNumbers
} from "src/graphql/generated/campaignPhoneNumberDetails";
import {
  campaignRotateJoinToken,
  campaignRotateJoinTokenVariables
} from "src/graphql/generated/campaignRotateJoinToken";
import {
  forceRotateCampaignPhoneNumber,
  forceRotateCampaignPhoneNumberVariables
} from "src/graphql/generated/forceRotateCampaignPhoneNumber";
import {
  getCampaign_campaign,
  getCampaign,
  getCampaign_campaign_outboundPhoneNumbers
} from "src/graphql/generated/getCampaign";
import { ApprovalRequirement } from "src/graphql/generated/globalTypes";
import { CampaignStatus, JobStatus } from "src/graphql/generated/globalTypes";
import {
  pauseCampaign,
  pauseCampaignVariables
} from "src/graphql/generated/pauseCampaign";
import {
  twilioSubaccountSelectForCampaign,
  twilioSubaccountSelectForCampaignVariables
} from "src/graphql/generated/twilioSubaccountSelectForCampaign";
import {
  unpauseCampaign,
  unpauseCampaignVariables
} from "src/graphql/generated/unpauseCampaign";
import { SELECT_TWILIO_SUBACCOUNT_FOR_CAMPAIGN } from "src/graphql/twilioSubaccount";
import {
  PAUSE_CAMPAIGN,
  UNPAUSE_CAMPAIGN
} from "src/pages/CampaignList/queries";
import formatPhoneNumber from "src/util/formatPhoneNumber";
import getCallerURL from "src/util/getCallerURL";
import useMutation from "src/util/useMutation";

import { numberAsStringField } from "../OrgSettings/common";
import { TwilioSubaccountSelectForm } from "../OrgSettings/twilioConfiguration/TwilioSelect";
import BandwidthVoiceWarning from "./BandwidthVoiceWarning";
import CallerMessaging from "./CallerMessaging";
import { CampaignAlgorithmForm } from "./CampaignAlgorithmForm";

const statusDescriptions: { [K in CampaignStatus]: string } = {
  [CampaignStatus.ACTIVE]: "running",
  [CampaignStatus.ARCHIVED]: "archived",
  [CampaignStatus.PERMANENTLY_ARCHIVED]: "archived",
  [CampaignStatus.DRAFT]: "not yet started",
  [CampaignStatus.PAUSED]: "paused",
  [CampaignStatus.PAUSING]: "pausing",
  [CampaignStatus.COMPLETE]: "complete",
  [CampaignStatus.STALLED]: "stalled"
};

const useStyles = makeStyles(theme => ({
  header: {
    margin: `${theme.spacing(3)}px 0 0`
  },
  text: {
    marginLeft: "5px"
  },
  formHeader: {
    margin: `0 0 ${theme.spacing(1)}px`
  },
  inlineForm: {
    margin: `0 0 ${theme.spacing(4)}px`
  },
  phoneNumberStaffControl: {
    marginRight: theme.spacing(2)
  }
}));

function StaffPhoneNumberControls({
  campaign
}: {
  campaign: getCampaign_campaign;
}) {
  const { loading, error, data } = useQuery<
    campaignPhoneNumberDetails,
    campaignPhoneNumberDetailsVariables
  >(GET_CAMPAIGN_PHONE_NUMBER_DETAILS, {
    variables: { campaignId: campaign.id }
  });

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <>
      {(data?.campaign?.outboundPhoneNumbers || []).map(number => (
        <StaffPhoneNumberControl campaign={campaign} number={number} />
      ))}
    </>
  );
}

function StaffPhoneNumberControl({
  campaign,
  number
}: {
  campaign: getCampaign_campaign;
  number: campaignPhoneNumberDetails_campaign_outboundPhoneNumbers;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [forceRotatePhoneNumber] = useMutation<
    forceRotateCampaignPhoneNumber,
    forceRotateCampaignPhoneNumberVariables
  >(FORCE_ROTATE_CAMPAIGN_PHONE_NUMBER, {
    variables: { campaignId: campaign.id, numberId: number.id }
  });

  const [autoRotateNumber] = useMutation<
    campaignEditAutoRotatablePhoneNumber,
    campaignEditAutoRotatablePhoneNumberVariables
  >(EDIT_ROTATABLE_CAMPAIGN_PHONE_NUMBER, {
    variables: {
      campaignId: campaign.id,
      numberId: number.id,
      autoRotate: !number.autoRotatable
    }
  });

  const [isRotating, setIsRotating] = useState(false);
  async function handleRotate() {
    if (isRotating) {
      return;
    }

    setIsRotating(true);

    const { errors } = await forceRotatePhoneNumber();
    setIsRotating(false);

    if (errors) {
      enqueueSnackbar(errors[0].message, { variant: "error" });
    }
  }

  async function handleAutoRotate() {
    if (isRotating) {
      return;
    }

    setIsRotating(true);

    const { errors } = await autoRotateNumber();
    setIsRotating(false);

    if (errors) {
      enqueueSnackbar(errors[0].message, { variant: "error" });
    }
  }

  function getPhoneNumberStatus(): string {
    if (!number) {
      return "Unknown";
    }

    const { rotateJob } = number;

    if (!rotateJob) {
      return "Healthy";
    }

    switch (rotateJob.status) {
      case JobStatus.CREATED:
      case JobStatus.RUNNING:
      case JobStatus.QUEUED:
        return `Rotation in progress (started at ${new Date(
          rotateJob.createTime
        ).toLocaleString()})`;
      case JobStatus.FAILED:
        return `Rotation failed (started at ${new Date(
          rotateJob.createTime
        ).toLocaleString()})`;
      case JobStatus.DONE:
        return "Rotation complete";
    }
  }

  return (
    <div key={number.id}>
      <p>
        <strong>Phone number: {number.phoneNumber}</strong>
      </p>
      <p>Phone number status: {getPhoneNumberStatus()}</p>
      <p>Calls made with phone number: {number.callsMade}</p>
      <p>Auto rotate number: {number.autoRotatable.toString()}</p>
      <p>STIR/SHAKEN approved: {number.stirShakenApproved.toString()}</p>
      <p>
        Voice Integrity approved: {number.voiceIntegrityApproved.toString()}
      </p>
      <div>
        <Button
          onClick={handleRotate}
          disabled={isRotating}
          className={classes.phoneNumberStaffControl}
          color={"primary"}
          variant="contained"
        >
          {isRotating ? "Rotating..." : "Force number rotation"}
        </Button>

        <Button
          onClick={handleAutoRotate}
          disabled={isRotating}
          className={classes.phoneNumberStaffControl}
          color={"primary"}
          variant="contained"
        >
          {isRotating
            ? "Updating..."
            : `Turn ${number.autoRotatable ? "Off" : "On"} Auto Rotation`}
        </Button>
      </div>
    </div>
  );
}

type PoolSizeValues = { poolSize: string };

function StaffEditPoolSize({ campaign }: { campaign: getCampaign_campaign }) {
  const snackbar = useSnackbar();
  const classes = useStyles();

  const schema = yup.object().shape({
    poolSize: yup.number().nullable().positive().integer()
  });

  const [editOutboundPoolSize] = useMutation<
    campaignEditOutboundPoolSize,
    campaignEditOutboundPoolSizeVariables
  >(EDIT_OUTBOUND_POOL_SIZE, {
    onErrorSnackbarMessage: `Failed to update outbound pool size.`
  });

  async function handleSubmit(
    values: PoolSizeValues,
    helpers: FormikHelpers<PoolSizeValues>
  ) {
    const newPoolSize = _.parseInt(values.poolSize);
    const { errors, data } = await editOutboundPoolSize({
      variables: { campaignId: campaign.id, newPoolSize }
    });

    if (!errors) {
      snackbar.enqueueSnackbar("Campaign updated.", { variant: "info" });
      helpers.resetForm({
        values: {
          poolSize: numberAsStringField(
            data!.campaignEditOutboundPoolSize.minimum_outgoing_number_pool_size
          )
        }
      });
    }
  }
  return (
    <Formik
      initialValues={{
        poolSize: numberAsStringField(
          campaign.minimum_outgoing_number_pool_size
        )
      }}
      onSubmit={handleSubmit}
      validationSchema={schema}
    >
      {({ isSubmitting, dirty }) => (
        <div className={classes.inlineForm}>
          <Form>
            <h3 className={classes.formHeader}>Outbound Number Pool</h3>
            <FormikErrorBanner />
            <FormikTextField
              fullWidth
              label="Minimum outbound pool size"
              name="poolSize"
              disabled={isSubmitting}
            />
            <SubmitButton
              type="submit"
              disabled={!dirty}
              isSubmitting={isSubmitting}
              submitText="Update"
              loadingText="Updating..."
            />
          </Form>
        </div>
      )}
    </Formik>
  );
}

function StalledCampaignAdvice({
  campaign
}: {
  campaign: getCampaign_campaign;
}) {
  if (campaign.status !== CampaignStatus.STALLED) return null;
  let advice: string;
  let strongAdvice: string;
  if (campaign.requiredApprovals) {
    advice =
      "The campaign is stalled because there are contacts that require approval to be called.";
    strongAdvice = "Use the Clicker URL below to approve more contacts.";
  } else {
    advice =
      "The campaign has stalled because not enough time has passed between passes.";
    const hoursTillRetry = Math.floor(campaign.no_call_window / 60.0);
    const minutesTillRetry = campaign.no_call_window % 60;
    const pluralize = (count: number, word: string) =>
      count && `${count} ${word}${count !== 1 ? "s" : ""}`;
    const timeTillRetry = _([
      pluralize(hoursTillRetry, "hour"),
      pluralize(minutesTillRetry, "minute")
    ])
      .compact()
      .join(" and ");
    strongAdvice = `You can start your next pass ${timeTillRetry} after your most recent attempt was completed.`;
  }
  return (
    <Alert severity="warning">
      {advice} <strong>{strongAdvice}</strong>
    </Alert>
  );
}

function OwnedPhoneNumbersStatus({
  campaign
}: {
  campaign: getCampaign_campaign;
}) {
  const admin = useAdmin()!;
  return (
    <>
      <p>Outbound phone numbers: </p>
      <>
        {campaign.outboundPhoneNumbers.map(ownedPhoneNumber => (
          <OwnedPhoneNumberStatus ownedPhoneNumber={ownedPhoneNumber} />
        ))}
      </>
      {admin.isStaff && (
        <Card>
          <CardHeader title="Staff Phone Number Controls" />
          <CardContent>
            <StaffEditPoolSize campaign={campaign} />
            <StaffPhoneNumberControls campaign={campaign} />
          </CardContent>
        </Card>
      )}
    </>
  );
}

function OwnedPhoneNumberStatus({
  ownedPhoneNumber
}: {
  ownedPhoneNumber: getCampaign_campaign_outboundPhoneNumbers;
}) {
  const rotationError = ownedPhoneNumber.rotationErrors?.[0];

  return (
    <div key={ownedPhoneNumber.id}>
      <p>
        <strong>{formatPhoneNumber(ownedPhoneNumber.phoneNumber)}</strong>
      </p>
      {rotationError && (
        <Alert severity="error">
          We're trying to rotate out this phone number, but encountered an
          error: {rotationError.message}
        </Alert>
      )}
    </div>
  );
}

const ActionButton = ({
  campaign,
  refreshCampaign
}: {
  campaign: getCampaign_campaign;
  refreshCampaign: () => Promise<ApolloQueryResult<getCampaign>>;
}) => {
  const confirm = useConfirm();
  const classes = useStyles();

  function makeHandler(
    campaignId: string,
    callback: (campaignId: string) => Promise<void>,
    {
      confirmTitle,
      confirmMessage
    }: { confirmTitle: string; confirmMessage: string }
  ) {
    return async () => {
      if (confirmTitle || confirmMessage) {
        try {
          await confirm({ title: confirmTitle, description: confirmMessage });
        } catch {
          // user cancelled
          return;
        }
      }
      await callback(campaignId);
    };
  }

  function makeCampaignStatusMutationCallback(
    mutation: MutationTuple<any, { id: string }>[0]
  ) {
    return async (campaignId: string) => {
      const { errors } = await mutation({ variables: { id: campaignId } });

      if (errors) {
        // The status mutation failed -- probably someone else changed the status
        // of the campaign out from under us. reloading for sync
        await refreshCampaign();
      }
    };
  }

  const [pauseCampaign] = useMutation<pauseCampaign, pauseCampaignVariables>(
    PAUSE_CAMPAIGN,
    {
      onErrorSnackbarMessage: "Could not pause campaign"
    }
  );

  const [unpauseCampaign] = useMutation<
    unpauseCampaign,
    unpauseCampaignVariables
  >(UNPAUSE_CAMPAIGN, {
    onErrorSnackbarMessage: "Could not unpause campaign"
  });

  switch (campaign.status) {
    case CampaignStatus.ACTIVE:
      return (
        <Button
          onClick={makeHandler(
            campaign.id,
            makeCampaignStatusMutationCallback(pauseCampaign),
            {
              confirmTitle: "Pause Campaign",
              confirmMessage: `Are you sure you want to pause the campaign "${campaign.name}"? Calls in progress will finish up, then all callers will be notified that the campaign is paused and will be disconnected. You can always resume the campaign later.`
            }
          )}
          color={"primary"}
          variant="contained"
        >
          <Tooltip title="Pause">
            <PauseIcon />
          </Tooltip>
          <span className={classes.text}>pause your campaign</span>
        </Button>
      );
    case CampaignStatus.PAUSED:
      return (
        <Button
          onClick={makeHandler(
            campaign.id,
            makeCampaignStatusMutationCallback(unpauseCampaign),
            {
              confirmTitle: "Resume Campaign",
              confirmMessage: `Are you sure you want to resume the campaign "${campaign.name}"?`
            }
          )}
          color={"primary"}
          variant="contained"
        >
          <Tooltip title="Resume">
            <UnpauseIcon />
          </Tooltip>
          <span className={classes.text}>resume your campaign</span>
        </Button>
      );
    default:
      return <></>;
  }
};

export default function StartCampaign({
  campaign,
  refreshCampaign,
  goToNextSection
}: {
  campaign: getCampaign_campaign;
  refreshCampaign: () => Promise<ApolloQueryResult<getCampaign>>;
  goToNextSection: () => void;
}): JSX.Element {
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [rotateJoinToken] = useMutation<
    campaignRotateJoinToken,
    campaignRotateJoinTokenVariables
  >(ROTATE_CAMPAIGN_JOIN_TOKEN, {
    variables: { campaignId: campaign.id }
  });

  const handleRotateJoinLink = async () => {
    const clickerUrlEnabled =
      campaign.approval_requirement !== ApprovalRequirement.NONE;

    try {
      await confirm({
        title: "Recreate Links",
        description: clickerUrlEnabled
          ? "Are you sure you want to recreate the campaign links? This will invalidate the current caller and clicker URLs."
          : "Are you sure you want to recreate the caller link? This will invalidate the current link."
      });
    } catch {
      // user cancelled
      return;
    }

    try {
      await rotateJoinToken();
      enqueueSnackbar("Links recreated", { variant: "info" });
    } catch (e) {
      console.error(e);
      enqueueSnackbar("Error recreating links", { variant: "error" });
    }
  };

  return (
    <>
      <h2>
        <strong>Campaign Status</strong>: This campaign is{" "}
        {statusDescriptions[campaign.status]}.
      </h2>
      <p>
        <ActionButton campaign={campaign} refreshCampaign={refreshCampaign} />
      </p>
      <BandwidthVoiceWarning campaign={campaign} />
      <StalledCampaignAdvice campaign={campaign} />

      <OwnedPhoneNumbersStatus campaign={campaign} />
      <StaffTwilioAccountControls campaign={campaign} />

      {campaign.status !== CampaignStatus.ARCHIVED && (
        <>
          <h3 className={classes.header}>Caller URL</h3>
          <ClickToCopy
            text={getCallerURL({ queryParams: { t: campaign.token! } })}
          />
          {campaign.ivr_phone_number && (
            <>
              <h3 className={classes.header}>Phone-only Number</h3>
              <ClickToCopy
                text={formatPhoneNumber(campaign.ivr_phone_number!)}
              />
            </>
          )}
          {campaign.approval_requirement !== ApprovalRequirement.NONE && (
            <>
              <h3 className={classes.header}>Clicker URL</h3>
              <ClickToCopy
                text={getCallerURL({
                  queryParams: { t: campaign.token!, a: "approve" }
                })}
              />
            </>
          )}
        </>
      )}
      <Box sx={{ my: 2 }}>
        {campaign.status === CampaignStatus.ACTIVE ? (
          <DisabledButtonWithTooltip
            tooltipTitle="Campaign must be paused to recreate links"
            variant="contained"
          >
            Recreate Links
          </DisabledButtonWithTooltip>
        ) : (
          <Button onClick={handleRotateJoinLink} variant="contained">
            Recreate Links
          </Button>
        )}
      </Box>
      <hr />
      <CallerMessaging campaign={campaign} goToNextSection={goToNextSection} />
    </>
  );
}

function StaffTwilioAccountControls({
  campaign
}: {
  campaign: getCampaign_campaign;
}): JSX.Element {
  const admin = useAdmin()!;
  const theme = useTheme();

  const [selectTwilioSubaccountForCampaign] = useMutation<
    twilioSubaccountSelectForCampaign,
    twilioSubaccountSelectForCampaignVariables
  >(SELECT_TWILIO_SUBACCOUNT_FOR_CAMPAIGN, {
    refetchQueries: ["getCampaign"],
    awaitRefetchQueries: true
  });

  const onSubmit = ({ twilioSubaccountId }: { twilioSubaccountId: string }) =>
    selectTwilioSubaccountForCampaign({
      variables: { twilioSubaccountId, campaignId: campaign.id }
    });

  return (
    <>
      {admin.isStaff && (
        <>
          <Card style={{ marginTop: theme.spacing(4) }}>
            <CardHeader title="Staff Provider Account Controls" />
            <CardContent>
              <>
                Provider: <strong>{_.upperFirst(campaign.provider)}</strong>
              </>

              {campaign.twilioSubaccount && (
                <>
                  <Alert
                    severity="warning"
                    style={{
                      marginBottom: theme.spacing(2),
                      marginTop: theme.spacing(3)
                    }}
                  >
                    Changing the campaign's Twilio Subaccount will temporarily
                    slow calling and may force some callers to reconnect.
                  </Alert>

                  <TwilioSubaccountSelectForm
                    onSubmit={onSubmit}
                    twilioSubaccountId={campaign.twilioSubaccount.id}
                  />
                </>
              )}
            </CardContent>
          </Card>
          <Card style={{ marginTop: theme.spacing(4) }}>
            <CardHeader title="Staff Algorithm Controls" />
            <CardContent>
              <CampaignAlgorithmForm campaign={campaign} />
            </CardContent>
          </Card>
        </>
      )}
    </>
  );
}
